import { ExchangeRateTab } from "@/modules/exchangeRate/common/enum";
import type { IExchangeRate } from "@/modules/exchangeRate/common/types";
import { getExchangeRateTabItems } from "@/modules/exchangeRate/helpers";
import { useGetExchangeRatesQuery } from "@/modules/exchangeRate/services";
import { CURRENCY } from "@/shared/common/constants";
import { AppPaths } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { EmptyOrErrorContent, SeeMore } from "@/shared/components";
import { getErrorMessage } from "@/shared/helpers";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Image } from "@ab.uitools/ui-library/components/Image";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { Tab } from "@ab.uitools/ui-library/components/Tab";
import { Text } from "@ab.uitools/ui-library/components/Text";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ExchangeRateAmount } from "./ExchangeRateAmount";

interface Props {
  isInWidget?: boolean;
  isSideSheetOpen?: boolean;
  hasSeeMore?: boolean;
  hasTitle?: boolean;
  rowCount?: number;
}

export const ExchangeRateList = ({
  isInWidget,
  isSideSheetOpen,
  hasSeeMore,
  hasTitle,
  rowCount,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedValue, setSelectedValue] = useState<string>(ExchangeRateTab.NonCash);

  const {
    data: exchangeRate,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetExchangeRatesQuery(
    {
      type: selectedValue,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const handleSeeMore = () => {
    navigate(AppPaths.ExchangeRate);
  };

  const exchangeRatePart = useMemo(() => {
    if (rowCount) {
      return exchangeRate?.slice(0, rowCount);
    }
    return exchangeRate;
  }, [exchangeRate]);

  useEffect(() => {
    if (isSideSheetOpen) {
      refetch();
    }
  }, [isSideSheetOpen]);

  return (
    <div
      className={classNames("exchange-rate", {
        ["widget exchange-rate-widget"]: isInWidget,
      })}>
      <div className="exchange-rate-body">
        <div className="flexbox full-width justify-content--between align-items--center">
          {hasTitle && (
            <div className="full-width flexbox align-items--center justify-content--between pt-12">
              <Text className="truncate" weight="bold" size="large">
                {t("exchangeRate.title")}
              </Text>
              {hasSeeMore && (
                <SeeMore action={handleSeeMore} className="ml-8"/>
              )}
            </div>
          )}
        </div>
        <div className="full-width tabs-section pb-20">
          <Tab
            size="medium"
            color="light"
            tabItems={getExchangeRateTabItems({ t })}
            selectedValue={selectedValue}
            onSelect={(value) => setSelectedValue(value as string)}
          />
        </div>
        <div className="full-width">
          {!isError && !exchangeRatePart?.length && !isFetching ? (
            <div className="full-width flexbox align-items--center pt-64 pb-64 mt-6 mb-6">
              <EmptyOrErrorContent isShowButton={false}/>
            </div>
          ) : isError && !isFetching ? (
            <div className="full-width flexbox align-items--center pt-64 pb-64 mt-6 mb-6">
              <EmptyOrErrorContent
                isError={true}
                title={t(getErrorMessage(error as IError))}
                action={refetch}
              />
            </div>
          ) : (
            isFetching ? (
              <div
                className="full-width flexbox justify-content--center align-items--center loader-wrapper">
                <Spinner isFullScreen={false} size="small"/>
              </div>
            ) : (
              <>
                <div className="full-width flexbox justify-content--between mb-8">
                  <Text className="exchange-rate-currency-col truncate pr-16" type="secondary">
                    {t("exchangeRate.currency")}
                  </Text>
                  <div className="flexbox justify-content--between exchange-rate-amount-cols">
                    <Text type="secondary" className="text-center truncate pr-8 w-50">
                      {t("exchangeRate.buy")}
                    </Text>
                    <Text className="text-right truncate w-50 pl-8" type="secondary">
                      {t("exchangeRate.sell")}
                    </Text>
                  </div>
                </div>
                {exchangeRatePart?.map((rate: IExchangeRate, i: number) => (
                  <div key={rate.currency}>
                    {!!i && (
                      <Divider
                        color="light"
                        type="primary"
                        isHorizontal
                      />
                    )}
                    <div className="full-width exchange-rate-row">
                      <div className="flexbox align-items--center exchange-rate-currency-col pr-16">
                        <Image
                          imagePath={CURRENCY[rate.currency]?.flag}
                        />
                        <Text type="secondary" size="medium" className="pl-12">
                          {rate.currency}
                        </Text>
                      </div>
                      <div className="flexbox justify-content--between exchange-rate-amount-cols">
                        <ExchangeRateAmount
                          className="pr-8 justify-content--center w-50"
                          amount={rate.buy}
                        />
                        <ExchangeRateAmount
                          className="pl-8 justify-content--end w-50"
                          amount={rate.sale}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ))}
        </div>
      </div>
    </div>
  );
};
